import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';

// Shared navbar for every public/marketing page (Home, Pricing, Features,
// Contact). Before this, each page hand-rolled its own nav (some with a
// BETA badge + back button, some with just a pricing link) -- genuinely
// inconsistent from page to page, and none of them had a mobile menu at
// all, just Tailwind `hidden md:flex` dropping links with no fallback.
// One component now, used everywhere, with a real hamburger panel below
// the 640px breakpoint (the app's own authenticated-side mobile pattern
// is a bottom tab bar, not a top hamburger -- not reusable here since
// these are public, unauthenticated pages with a different link set).
//
// Anchor links (How it works / Credibility / Groups) only exist as
// sections on the homepage. From any other page they navigate to
// `/#id`; HomePage.jsx's own effect below picks up `location.hash` on
// mount and scrolls to it once the section has actually rendered.
const NAV_LINKS = [
  { key: 'howItWorks', hash: 'how-it-works' },
  { key: 'credibility', hash: 'credibility' },
  { key: 'groups', hash: 'groups' },
];

const PublicNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile panel on any route change so it never lingers open
  // after a navigation triggered from inside it.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const goToSection = (hash) => {
    if (location.pathname === '/') {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/#${hash}`);
    }
    setMobileOpen(false);
  };

  const goTo = (path) => { navigate(path); setMobileOpen(false); };

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 50,
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      backdropFilter: 'blur(20px)',
      // This bar is intentionally always-dark regardless of site theme (the
      // logo/CTA treatment is tuned for it). At 0.82 opacity it let ~18% of
      // a light-mode page's white bleed through the blur, reading as a
      // washed-out grey instead of black -- confirmed live 2026-09-11.
      // Pushed near-opaque to match the mobile panel's own 0.97 below.
      background: 'rgba(9,9,15,0.97)',
    }}>
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <img
          src="/StockerLogo.png"
          alt="Stocker"
          style={{ height: 30, width: 'auto', cursor: 'pointer' }}
          onClick={() => goTo('/')}
        />

        {/* Desktop */}
        <div className="pubnav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            {NAV_LINKS.map((l) => (
              <button key={l.key} className="pubnav-link" onClick={() => goToSection(l.hash)}>{t(`home.nav.${l.key}`)}</button>
            ))}
            <button className="pubnav-link" onClick={() => goTo('/features')}>{t('home.nav.features')}</button>
            <button className="pubnav-link" onClick={() => goTo('/pricing')}>{t('home.nav.pricing')}</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button className="pubnav-ghost" onClick={() => goTo('/login')}>{t('home.nav.login')}</button>
            <button className="pubnav-cta" onClick={() => goTo('/register')}>{t('home.nav.getStarted')}</button>
          </div>
        </div>

        {/* Mobile trigger */}
        <button
          className="pubnav-burger"
          aria-label={mobileOpen ? t('home.nav.closeMenuAria') : t('home.nav.openMenuAria')}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X size={22} color="#e2e8f0" /> : <Menu size={22} color="#e2e8f0" />}
        </button>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          background: 'rgba(9,9,15,0.97)',
          padding: '8px 24px 20px',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {NAV_LINKS.map((l) => (
              <button key={l.key} className="pubnav-link-mobile" onClick={() => goToSection(l.hash)}>{t(`home.nav.${l.key}`)}</button>
            ))}
            <button className="pubnav-link-mobile" onClick={() => goTo('/features')}>{t('home.nav.features')}</button>
            <button className="pubnav-link-mobile" onClick={() => goTo('/pricing')}>{t('home.nav.pricing')}</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button className="pubnav-cta" style={{ justifyContent: 'center' }} onClick={() => goTo('/register')}>{t('home.nav.getStarted')}</button>
            <button className="pubnav-ghost" style={{ textAlign: 'center', padding: '10px 0' }} onClick={() => goTo('/login')}>{t('home.nav.login')}</button>
          </div>
        </div>
      )}

      <style>{`
        .pubnav-link {
          color: #64748b; font-size: 13.5px; font-weight: 500; text-decoration: none;
          transition: color 0.15s ease; background: none; border: none; cursor: pointer; font-family: inherit;
        }
        .pubnav-link:hover { color: #cbd5e1; }
        .pubnav-link-mobile {
          color: #cbd5e1; font-size: 15px; font-weight: 500; text-align: left;
          background: none; border: none; cursor: pointer; font-family: inherit;
          padding: 12px 0; border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .pubnav-ghost {
          color: #94a3b8; font-size: 14px; font-weight: 500; text-decoration: none;
          transition: color 0.15s ease; background: none; border: none; cursor: pointer; font-family: inherit;
        }
        .pubnav-ghost:hover { color: #e2e8f0; }
        .pubnav-cta {
          display: inline-flex; align-items: center; gap: 8px;
          background: #7c3aed; color: #fff; font-size: 13.5px; font-weight: 600;
          padding: 9px 18px; border-radius: 9px; text-decoration: none;
          transition: background 0.15s ease; border: 1px solid rgba(255,255,255,0.08);
          font-family: inherit; cursor: pointer;
        }
        .pubnav-cta:hover { background: #6d28d9; }
        .pubnav-burger {
          display: none; background: none; border: none; cursor: pointer; padding: 8px; margin: -8px;
        }
        @media (max-width: 860px) {
          .pubnav-desktop { display: none !important; }
          .pubnav-burger { display: flex !important; align-items: center; justify-content: center; }
        }
      `}</style>
    </nav>
  );
};

export default PublicNav;
